import React, { useEffect, useMemo, useState } from 'react';
import './UserProfile.css';
import { auth } from './firebase';
import {
  onAuthStateChanged, updateProfile, updatePassword,
  reauthenticateWithCredential, reauthenticateWithPopup,
  EmailAuthProvider, GoogleAuthProvider, sendPasswordResetEmail,
} from 'firebase/auth';
import { obtenerMiPerfil, actualizarMiPerfil } from './api/perfil';
import { toast } from 'react-toastify';
import {
  User, Mail, Calendar, Save, Lock, ShieldCheck,
  Image as ImageIcon, Building2, Phone, Info,
} from 'lucide-react';

function UserProfile() {
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [perfilExtra, setPerfilExtra] = useState({ empresa: '', telefono: '' });
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNueva, setPasswordNueva] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loadingPerfil, setLoadingPerfil] = useState(false);
  const [loadingPwd, setLoadingPwd] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (u) {
        setDisplayName(u.displayName || '');
        setPhotoURL(u.photoURL || '');
        try {
          const perfil = await obtenerMiPerfil();
          setPerfilExtra({ empresa: perfil?.empresa || '', telefono: perfil?.telefono || '' });
        } catch (e) { console.error(e); }
      }
    });
    return () => unsub();
  }, []);

  const proveedorPrincipal = useMemo(
    () => user?.providerData?.[0]?.providerId || 'password', [user]
  );

  const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleString('es-CL'); } catch { return String(val); }
  };

  const guardarPerfil = async () => {
    if (!user) return toast.error('No hay usuario autenticado');
    setLoadingPerfil(true);
    try {
      await updateProfile(user, { displayName: displayName.trim(), photoURL: photoURL.trim() || null });
      await actualizarMiPerfil((perfilExtra.empresa || '').trim(), (perfilExtra.telefono || '').trim());
      toast.success('Perfil actualizado correctamente');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar el perfil');
    } finally {
      setLoadingPerfil(false);
    }
  };

  const cambiarPassword = async (e) => {
    e?.preventDefault?.();
    if (!user) return toast.error('No hay usuario autenticado');
    if (proveedorPrincipal === 'password' && !passwordActual)
      return toast.warning('Debes ingresar tu contraseña actual');
    if (!passwordNueva || !passwordConfirm)
      return toast.warning('Completa la nueva contraseña y su confirmación');
    if (passwordNueva !== passwordConfirm)
      return toast.error('Las contraseñas no coinciden');
    if (passwordNueva.length < 6)
      return toast.warning('La nueva contraseña debe tener al menos 6 caracteres');

    setLoadingPwd(true);
    try {
      if (proveedorPrincipal === 'password') {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, passwordActual));
      } else if (proveedorPrincipal === 'google.com') {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
      } else {
        toast.error('Debes reautenticarte con tu proveedor de acceso original.');
        setLoadingPwd(false);
        return;
      }
      await updatePassword(user, passwordNueva);
      setPasswordActual(''); setPasswordNueva(''); setPasswordConfirm('');
      toast.success('Contraseña actualizada correctamente');
    } catch (error) {
      console.error(error);
      const msgs = {
        'auth/wrong-password': 'La contraseña actual es incorrecta',
        'auth/invalid-credential': proveedorPrincipal === 'password'
          ? 'Credencial inválida. Verifica tu contraseña actual.'
          : 'Reautenticación fallida. Intenta nuevamente.',
        'auth/weak-password': 'La nueva contraseña es demasiado débil',
        'auth/requires-recent-login': 'Por seguridad, vuelve a iniciar sesión y reintenta',
      };
      toast.error(msgs[error.code] || 'No se pudo actualizar la contraseña');
    } finally {
      setLoadingPwd(false);
    }
  };

  const enviarReset = async () => {
    if (!user?.email) return toast.error('No hay correo asociado');
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.info('Se envió un correo para restablecer la contraseña');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo enviar el correo de restablecimiento');
    }
  };

  return (
    <div className="perfil-main">
      <div className="perfil-wrapper">

        {/* Título */}
        <div className="perfil-page-title">
          <div className="perfil-page-icon"><User size={18} /></div>
          <div>
            <h1 className="perfil-page-heading">Mi perfil</h1>
            <p className="perfil-page-sub">Gestiona tu información personal y seguridad</p>
          </div>
        </div>

        {!user ? (
          <div className="perfil-loading">Cargando usuario…</div>
        ) : (
          <div className="perfil-grid">

            {/* ── Card: datos básicos ── */}
            <div className="perfil-card">
              <div className="perfil-card-header">
                <div className="perfil-card-icon"><User size={13} /></div>
                <span className="perfil-card-title">Información básica</span>
              </div>
              <div className="perfil-card-body">

                {/* Avatar + resumen */}
                <div className="perfil-avatar-section">
                  <div className="perfil-avatar">
                    {photoURL
                      ? <img src={photoURL} alt="avatar" />
                      : <div className="perfil-avatar-placeholder"><User size={28} /></div>
                    }
                  </div>
                  <div className="perfil-avatar-info">
                    <p className="perfil-nombre">{user.displayName || 'Sin nombre'}</p>
                    <div className="perfil-meta"><Mail size={12} />{user.email || '—'}</div>
                  </div>
                </div>

                {/* Info readonly */}
                <div className="perfil-info-row">
                  <span className="perfil-info-row-icon"><Calendar size={13} /></span>
                  <span className="perfil-info-row-label">Creado</span>
                  <span>{fmtDate(user.metadata?.creationTime)}</span>
                </div>
                <div className="perfil-info-row">
                  <span className="perfil-info-row-icon"><Calendar size={13} /></span>
                  <span className="perfil-info-row-label">Último acceso</span>
                  <span>{fmtDate(user.metadata?.lastSignInTime)}</span>
                </div>

                {/* Editar nombre */}
                <div className="perfil-field">
                  <span className="perfil-field-label">Nombre para mostrar</span>
                  <input
                    type="text"
                    className="perfil-input"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Tu nombre"
                  />
                </div>

                {/* Editar foto */}
                <div className="perfil-field">
                  <span className="perfil-field-label">URL de la foto</span>
                  <div className="perfil-input-icon">
                    <span className="perfil-input-icon-symbol"><ImageIcon size={13} /></span>
                    <input
                      type="url"
                      value={photoURL}
                      onChange={e => setPhotoURL(e.target.value)}
                      placeholder="https://…"
                    />
                  </div>
                </div>

                <div className="perfil-actions">
                  <button className="perfil-btn perfil-btn--primary" onClick={guardarPerfil} disabled={loadingPerfil}>
                    <Save size={13} /> {loadingPerfil ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Card: información adicional ── */}
            <div className="perfil-card">
              <div className="perfil-card-header">
                <div className="perfil-card-icon"><Building2 size={13} /></div>
                <span className="perfil-card-title">Información adicional</span>
              </div>
              <div className="perfil-card-body">
                <div className="perfil-form-row">
                  <div className="perfil-field">
                    <span className="perfil-field-label">Empresa</span>
                    <div className="perfil-input-icon">
                      <span className="perfil-input-icon-symbol"><Building2 size={13} /></span>
                      <input
                        type="text"
                        value={perfilExtra.empresa || ''}
                        onChange={e => setPerfilExtra({ ...perfilExtra, empresa: e.target.value })}
                        placeholder="Ej: Autostar"
                      />
                    </div>
                  </div>

                  <div className="perfil-field">
                    <span className="perfil-field-label">Teléfono</span>
                    <div className="perfil-input-icon">
                      <span className="perfil-input-icon-symbol"><Phone size={13} /></span>
                      <input
                        type="text"
                        value={perfilExtra.telefono || ''}
                        onChange={e => setPerfilExtra({ ...perfilExtra, telefono: e.target.value })}
                        placeholder="+56 9 1234 5678"
                      />
                    </div>
                  </div>
                </div>

                <div className="perfil-actions">
                  <button className="perfil-btn perfil-btn--primary" onClick={guardarPerfil} disabled={loadingPerfil}>
                    <Save size={13} /> {loadingPerfil ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Card: cambio de contraseña (full width) ── */}
            <div className="perfil-card perfil-card--full">
              <div className="perfil-card-header">
                <div className="perfil-card-icon"><Lock size={13} /></div>
                <span className="perfil-card-title">Cambiar contraseña</span>
              </div>
              <div className="perfil-card-body">

                {proveedorPrincipal !== 'password' && (
                  <div className="perfil-pwd-note">
                    <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Iniciaste sesión con <span className="perfil-provider-badge">{proveedorPrincipal}</span>.
                      Se abrirá un popup para reautenticarte con ese proveedor antes de cambiar la contraseña.
                    </span>
                  </div>
                )}

                <form onSubmit={cambiarPassword} style={{ display: 'contents' }}>
                  <div className="perfil-form-row">
                    {proveedorPrincipal === 'password' && (
                      <div className="perfil-field">
                        <span className="perfil-field-label">Contraseña actual</span>
                        <input
                          type="password"
                          className="perfil-input"
                          placeholder="••••••••"
                          value={passwordActual}
                          onChange={e => setPasswordActual(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="perfil-field">
                      <span className="perfil-field-label">Nueva contraseña</span>
                      <input
                        type="password"
                        className="perfil-input"
                        placeholder="Mín. 6 caracteres"
                        value={passwordNueva}
                        onChange={e => setPasswordNueva(e.target.value)}
                      />
                    </div>

                    <div className="perfil-field">
                      <span className="perfil-field-label">Confirmar contraseña</span>
                      <input
                        type="password"
                        className="perfil-input"
                        placeholder="Repite la nueva contraseña"
                        value={passwordConfirm}
                        onChange={e => setPasswordConfirm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="perfil-actions">
                    <button type="submit" className="perfil-btn perfil-btn--primary" disabled={loadingPwd}>
                      <ShieldCheck size={13} /> {loadingPwd ? 'Actualizando…' : 'Actualizar contraseña'}
                    </button>
                    <button type="button" className="perfil-btn" onClick={enviarReset} disabled={loadingPwd}>
                      Enviar correo de reseteo
                    </button>
                  </div>
                </form>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;